import React, { useState, useEffect, useCallback } from "react"
import {
  Typography,
  Paper,
  Container,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material"
import { useNavigate, useParams } from "react-router-dom"
import { useAuthUser } from "react-auth-kit"
import api from "../../../../utils/api"

interface Question {
  id: string
  description: string
}

interface Option {
  id: string
  questionId: string
  option: string
}

function InstanceResultsPage() {
  const { id } = useParams<{ id: string }>()
  const [loading, setLoading] = useState<boolean>(true);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [options, setOptions] = useState<Option[]>([])
  const [voteCounts, setVoteCounts] = useState<Record<string, Record<string, number>>>({});
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate()
  const auth = useAuthUser()
  const eventId = auth()?.eventId?._path?.segments[1] ?? "null"

    
  // all instance ids from eventId
  useEffect(() => {
    if (eventId) {
      api
        .get(`/admin/events/${eventId}/voting-instances-ids`)
        .then((response) => {
          const instances = response.data
          const exists = instances.some((instance: any) => instance.id === id)
          if (!exists) {
            navigate(
              "/not-authorized?message=This%20instance%20does%20not%20exist"
            )
          }
        })
        .catch((error) => {
          console.error("Error fetching voting instances:", error)
        })
    }
  }, [eventId, id, navigate])
  


  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(`/admin/instances/${id}/details`)
      const data = response.data
      if (data.questions && Array.isArray(data.questions)) {
         // Sort the questions based on created at time
         const questionData = data.questions
        const sortedQuestions = questionData.sort((a: any, b: any) => {
          const aSeconds = a.createdAt?._seconds || 0
          const bSeconds = b.createdAt?._seconds || 0
          return aSeconds - bSeconds
        })
        setQuestions(sortedQuestions)
      }
        setOptions(data.answers) // Set fallback value for options
        } catch (error) {
          console.error("Error fetching questions and answers:", error)
        }
      }, [id])

      // Fetch voters for the instance
      const fetchVotes = useCallback(async () => {
        try {
          const response = await api.get(`/admin/instances/${id}/results`);
          const { voteCounts } = response.data;
          setVoteCounts(voteCounts || {});
          console.log('Vote counts:', voteCounts);
        } catch (error) {
          console.error("Error fetching votes:", error);
          setError(error instanceof Error ? error.message : 'Failed to fetch vote counts');
        }
      }, [id]);

  // all instance ids from eventId
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchData(), fetchVotes()])
      .then(() => setLoading(false))
      .catch((error) => {
        console.error("Error fetching data:", error);
        setLoading(false);
      });
  }, [fetchData, fetchVotes]);
  
  return (
    <Container maxWidth="lg">
      <Typography variant="h3" align="center" gutterBottom>
        Admin Results
      </Typography>
      {loading ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ height: "80vh" }}
        >
          <CircularProgress />
        </Grid>
      ):(
      questions.map((question) => (
          <Paper
            key={question.id}
            style={{ margin: "20px 0", padding: "20px" }}
          >
            <Typography variant="h5">{question.description}</Typography>
            <Grid container spacing={2} mt={2} justifyContent="center">
              {options
                .filter((option) => option.questionId === question.id)
                .map((option) => {
                  const voteCount =
                    (voteCounts && voteCounts[question.id]?.[option.id]) || 0            
                      const maxVotes = Math.max(
                        ...options
                          .filter((opt) => opt.questionId === question.id)
                          .map((opt) => {
                            const votesForOption =
                              voteCounts && voteCounts[question.id]
                                ? voteCounts[question.id][opt.id]
                                : 0
                            return votesForOption || 0
                          })
                      );

                      const bgColor =
                      voteCount === maxVotes && voteCount !== 0 ? "green" : "grey"
                    const textColor =
                      voteCount === maxVotes && voteCount !== 0
                        ? "white"
                        : "inherit";

                        return (
                          <Grid item key={option.id} xs={12} sm={6} md={4} lg={3}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              justifyContent="center"
                              p={2}
                              bgcolor={bgColor}
                              borderRadius={2}
                              width="100%"
                              height="120px"
                              color={textColor}
                            >
                              <Typography variant="subtitle1" noWrap>
                                {option.option}
                              </Typography>
      
                              <Typography variant="subtitle1" noWrap>
                                {voteCount} {voteCount === 1 ? "vote" : "votes"}
                              </Typography>
                            </Box>
                          </Grid>
                          )
                        })}
                    </Grid>
                  </Paper>
                  ))
                )}
          </Container>
              )
            }
export default InstanceResultsPage
